import { useState } from 'react';
import type { CharacterBenefits } from '../../../hooks/useCharacterBenefits';
import type { PlaySession } from '../../../stores/playSessionStore';
import type { ConditionName } from '@character-creator/types';
import type { EquipmentData } from './types';
import { COIN_NAMES } from './constants';
import { ActiveEffectsSection } from '../ActiveEffectsSection';

interface ResourcesColumnProps {
  benefits: CharacterBenefits;
  session: PlaySession | null;
  equipment: EquipmentData;
  onDeathSaveSuccess: () => void;
  onDeathSaveFailure: () => void;
  onUseHitDie: (dieType: string) => number | null;
  onUseSpellSlot: (level: string) => void;
  onRestoreSpellSlot: (level: string) => void;
  onToggleCondition: (condition: ConditionName) => void;
  onUseResource: (resourceName: string, amount?: number) => void;
  onRestoreResource: (resourceName: string, amount?: number) => void;
  onToggleBardicInspiration: () => void;
  onToggleDmInspiration: () => void;
  onRemoveCustomEffect: (id: string) => void;
}

export function ResourcesColumn({
  benefits,
  session,
  equipment,
  onDeathSaveSuccess,
  onDeathSaveFailure,
  onUseHitDie,
  onUseSpellSlot,
  onRestoreSpellSlot,
  onToggleCondition,
  onUseResource,
  onRestoreResource,
  onToggleBardicInspiration,
  onToggleDmInspiration,
  onRemoveCustomEffect,
}: ResourcesColumnProps) {
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  return (
    <div className="space-y-3">
      {/* Death Saves */}
      <div className="bg-background-card border border-background-elevated rounded-lg overflow-hidden">
        <div className="bg-background-elevated px-3 py-2 font-display text-xs text-accent-gold uppercase tracking-wider">
          Death Saves
        </div>
        <div className="p-3 flex justify-around">
          <div>
            <div className="text-[10px] text-text-muted mb-1">Successes</div>
            <div className="flex gap-1">
              {[0, 1, 2].map((i) => (
                <button
                  key={i}
                  onClick={onDeathSaveSuccess}
                  className={`w-4 h-4 rounded-full border-2 transition-colors ${
                    (session?.deathSaveSuccesses ?? 0) > i
                      ? 'bg-success border-success'
                      : 'border-text-muted hover:border-success'
                  }`}
                />
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-text-muted mb-1">Failures</div>
            <div className="flex gap-1">
              {[0, 1, 2].map((i) => (
                <button
                  key={i}
                  onClick={onDeathSaveFailure}
                  className={`w-4 h-4 rounded-full border-2 transition-colors ${
                    (session?.deathSaveFailures ?? 0) > i
                      ? 'bg-error border-error'
                      : 'border-text-muted hover:border-error'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Hit Dice */}
      <div className="bg-background-card border border-background-elevated rounded-lg overflow-hidden">
        <div className="bg-background-elevated px-3 py-2 font-display text-xs text-accent-gold uppercase tracking-wider">
          Hit Dice
        </div>
        <div className="p-3">
          {benefits.hitDice.map(({ die, count }) => {
            const used = session?.hitDice[die]?.used ?? 0;
            return (
              <div key={die} className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">{die}</span>
                <div className="flex gap-1">
                  {Array.from({ length: count }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => onUseHitDie(die)}
                      className={`w-3 h-3 rounded border-2 transition-colors ${
                        i < used
                          ? 'border-text-muted'
                          : 'border-accent-gold bg-accent-gold'
                      }`}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Spell Slots */}
      {benefits.spellcasting && (
        <div className="bg-background-card border border-background-elevated rounded-lg overflow-hidden">
          <div className="bg-background-elevated px-3 py-2 font-display text-xs text-accent-gold uppercase tracking-wider">
            Spell Slots
          </div>
          <div className="p-3 flex flex-wrap gap-3 justify-center">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((level) => {
              const slot = session?.spellSlots[level];
              if (!slot || slot.max === 0) return null;
              const ordinal = level === '1' ? '1st' : level === '2' ? '2nd' : level === '3' ? '3rd' : `${level}th`;
              return (
                <div key={level} className="text-center min-w-[36px]">
                  <div className="text-[10px] text-text-muted mb-1">{ordinal}</div>
                  <div className="flex flex-col items-center gap-1">
                    {Array.from({ length: slot.max }).map((_, i) => (
                      <button
                        key={i}
                        onClick={() =>
                          i < slot.used ? onRestoreSpellSlot(level) : onUseSpellSlot(level)
                        }
                        className={`w-4 h-4 rounded-full border-2 transition-colors ${
                          i < slot.used
                            ? 'border-text-muted'
                            : 'border-class-wizard bg-class-wizard'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Active Effects (conditions, inspirations, custom effects) */}
      <ActiveEffectsSection
        session={session}
        benefits={benefits}
        onToggleCondition={onToggleCondition}
        onToggleBardicInspiration={onToggleBardicInspiration}
        onToggleDmInspiration={onToggleDmInspiration}
        onRemoveCustomEffect={onRemoveCustomEffect}
      />

      {/* Class Resources */}
      {session?.classResources && Object.keys(session.classResources).length > 0 && (
        <div className="bg-background-card border border-background-elevated rounded-lg overflow-hidden">
          <div className="bg-background-elevated px-3 py-2 font-display text-xs text-accent-gold uppercase tracking-wider">
            Class Resources
          </div>
          <div className="p-2 space-y-2">
            {Object.entries(session.classResources).map(([name, resource]) => (
              <div key={name} className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">{name}</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onRestoreResource(name)}
                    disabled={resource.used === 0}
                    className="w-5 h-5 flex items-center justify-center bg-accent-gold/20 text-accent-gold
                      rounded text-xs disabled:opacity-30 hover:bg-accent-gold/30 transition-colors"
                  >
                    +
                  </button>
                  <div className="flex gap-1">
                    {Array.from({ length: resource.max }).map((_, i) => (
                      <div
                        key={i}
                        className={`w-3 h-3 rounded-full border-2 transition-colors ${
                          i < resource.max - resource.used
                            ? 'border-class-sorcerer bg-class-sorcerer'
                            : 'border-text-muted'
                        }`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={() => onUseResource(name)}
                    disabled={resource.used >= resource.max}
                    className="w-5 h-5 flex items-center justify-center bg-text-muted/20 text-text-muted
                      rounded text-xs disabled:opacity-30 hover:bg-text-muted/30 transition-colors"
                  >
                    −
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Features */}
      <div className="bg-background-card border border-background-elevated rounded-lg overflow-hidden">
        <div className="bg-background-elevated px-3 py-2 flex items-center justify-between">
          <span className="font-display text-xs text-accent-gold uppercase tracking-wider">
            Features ({benefits.classFeatures.length})
          </span>
          {benefits.classFeatures.length > 8 && (
            <button
              onClick={() => setShowAllFeatures(!showAllFeatures)}
              className="text-[10px] text-accent-bronze hover:text-accent-gold transition-colors"
            >
              {showAllFeatures ? 'Show less' : 'Show all'}
            </button>
          )}
        </div>
        <div className={`p-2 overflow-y-auto ${showAllFeatures ? 'max-h-96' : 'max-h-48'}`}>
          {(showAllFeatures ? benefits.classFeatures : benefits.classFeatures.slice(0, 8)).map((feature) => (
            <div key={feature.id} className="py-2 border-b border-background-elevated last:border-b-0">
              <div className="text-sm font-semibold text-text-primary">{feature.name}</div>
              <div className="text-[10px] text-text-muted">Level {feature.level}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Equipment */}
      <div className="bg-background-card border border-background-elevated rounded-lg overflow-hidden">
        <div className="bg-background-elevated px-3 py-2 font-display text-xs text-accent-gold uppercase tracking-wider">
          Equipment
        </div>
        <div className="p-3 space-y-3">
          {/* Weapons from attacks */}
          {equipment.attacks.length > 0 && (
            <div>
              <div className="text-[10px] text-text-muted uppercase mb-1">Weapons</div>
              <div className="space-y-1">
                {equipment.attacks.map((attack, i) => (
                  <div key={i} className="text-xs text-text-secondary flex justify-between">
                    <span>{attack.name}</span>
                    <span className="text-text-muted">{attack.damage}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Currency */}
          {Object.entries(equipment.currency).some(([, val]) => val > 0) && (
            <div>
              <div className="text-[10px] text-text-muted uppercase mb-1">Currency</div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(equipment.currency).map(([coin, amount]) => {
                  if (amount <= 0) return null;
                  const coinInfo = COIN_NAMES[coin];
                  return (
                    <span key={coin} className="text-xs">
                      <span className={`font-mono font-bold ${coinInfo?.color || 'text-text-primary'}`}>
                        {amount}
                      </span>
                      <span className="text-text-muted ml-1">{coin}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Custom Equipment */}
          {equipment.customEquipment.length > 0 && (
            <div>
              <div className="text-[10px] text-text-muted uppercase mb-1">Gear</div>
              <div className="text-xs text-text-secondary">
                {equipment.customEquipment.join(', ')}
              </div>
            </div>
          )}

          {/* Empty state */}
          {equipment.attacks.length === 0 &&
           equipment.customEquipment.length === 0 &&
           !Object.entries(equipment.currency).some(([, val]) => val > 0) && (
            <div className="text-sm text-text-muted text-center py-2">
              No equipment
            </div>
          )}
        </div>
      </div>

      {/* Proficiencies */}
      <div className="bg-background-card border border-background-elevated rounded-lg overflow-hidden">
        <div className="bg-background-elevated px-3 py-2 font-display text-xs text-accent-gold uppercase tracking-wider">
          Proficiencies
        </div>
        <div className="p-3 space-y-3 text-xs">
          {/* Saving Throws */}
          <div>
            <div className="text-[10px] text-text-muted uppercase mb-1">Saving Throws</div>
            <div className="text-text-secondary">
              {benefits.savingThrows.length > 0 ? (
                benefits.savingThrows
                  .map((st) => st.charAt(0).toUpperCase() + st.slice(1, 3).toUpperCase())
                  .join(', ')
              ) : (
                <span className="text-text-muted italic">None</span>
              )}
            </div>
          </div>

          {/* Armor */}
          {benefits.armor && benefits.armor.length > 0 && (
            <div>
              <div className="text-[10px] text-text-muted uppercase mb-1">Armor</div>
              <div className="text-text-secondary">
                {benefits.armor.map((a) => a.charAt(0).toUpperCase() + a.slice(1)).join(', ')}
              </div>
            </div>
          )}

          {/* Weapons */}
          {benefits.weapons && benefits.weapons.length > 0 && (
            <div>
              <div className="text-[10px] text-text-muted uppercase mb-1">Weapons</div>
              <div className="text-text-secondary">
                {benefits.weapons.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(', ')}
              </div>
            </div>
          )}

          {/* Tools */}
          {benefits.tools && benefits.tools.length > 0 && (
            <div>
              <div className="text-[10px] text-text-muted uppercase mb-1">Tools</div>
              <div className="text-text-secondary">
                {benefits.tools.join(', ')}
              </div>
            </div>
          )}

          {/* Languages */}
          {benefits.languages && benefits.languages.length > 0 && (
            <div>
              <div className="text-[10px] text-text-muted uppercase mb-1">Languages</div>
              <div className="text-text-secondary">
                {benefits.languages.join(', ')}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
